import { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { LuSparkles, LuX } from 'react-icons/lu';
import { MdLink, MdLinkOff, MdDescription } from 'react-icons/md';
import type { AgentApiProvidedJobPostsRecord } from '@/types/serializers';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/Button';
import { showSuccessToast, showErrorToast } from '@/features/agent/job_change_processes/lib/toast';
import RunStatusPanel from '@/features/agent/candidates/ai_recommendations/components/RunStatusPanel';
import RecommendationList from '@/features/agent/candidates/ai_recommendations/components/RecommendationList';
import SuggestionConfirmDialog from '@/features/agent/candidates/ai_recommendations/components/SuggestionConfirmDialog';
import type { ReferenceSelection } from '@/features/agent/candidates/ai_recommendations/types/referenceSelection';
import useSuggestAiRecommendations from '@/features/agent/candidates/ai_recommendations/api/useSuggestAiRecommendations';
import useImportSharedJobPosts from '@/features/agent/shared_job_posts/api/useImportSharedJobPosts';
import ShareContentDialog from '@/features/agent/provided_job_posts/components/ShareContentDialog';
import usePdf from '@/features/agent/provided_job_posts/hooks/usePdf';
import GenerateIcon from './GenerateIcon';
import StepIndicator from './StepIndicator';
import ResumeUploadStep from './ResumeUploadStep';
import TextInputStep from './TextInputStep';
import CandidateSelectStep from './CandidateSelectStep';
import CustomizeFab from './CustomizeFab';
import CustomizeModal, {
  type MatchingMethod,
  type CustomizeResearchPayload,
} from './CustomizeModal';
import type { ResumeField } from './useUploadPersonaFile';
import useAiJobMatchingFlow from './useAiJobMatchingFlow';
import useFetchIam from '@/features/agent/api/useFetchIam';

const EMPTY_SELECTION: ReferenceSelection = { customFieldIds: [], interviewLogIds: [] };

const ASSET_BASE = '/agent/provided_job_posts/ai_job_matching';

// ステップ1（方法を選ぶ）は Figma の実寸で組み、収まらないビューポートでは等倍縮小する
const METHOD_SELECT_WIDTH = 990;
const METHOD_SELECT_HEIGHT = 555;
const VIEWPORT_MARGIN = 32;

const METHODS: {
  key: MatchingMethod;
  title: string;
  description: string;
  image: string;
  width: number;
  height: number;
}[] = [
  {
    key: 'resume',
    title: '履歴書',
    description: '履歴書のPDFをアップロードして、AIが自動で情報を読み取ります。',
    image: `${ASSET_BASE}/method-resume.png`,
    width: 128,
    height: 130,
  },
  {
    key: 'candidate',
    title: '登録済みの求職者',
    description: 'すでに登録済みの求職者から、AIが自動で情報を読み取ります。',
    image: `${ASSET_BASE}/method-candidate.png`,
    width: 143,
    height: 144,
  },
  {
    key: 'text',
    title: '面談の文字起こし',
    description: '面談情報や推薦文などのテキストから、AIが自動で情報を読み取ります。',
    image: `${ASSET_BASE}/method-text.svg`,
    width: 116,
    height: 117,
  },
];

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export default function AiJobMatchingModal({ open, onOpenChange }: Props) {
  // 求職者機能が制限されたプランでは「登録済みの求職者」を出さない。バックエンドの job_feature_restricted? と揃える (Issue #3324)
  const { data: iam } = useFetchIam();
  const isJobFeatureRestricted = ['trial', 'job_only'].includes(iam?.plan_type ?? '');
  const availableMethods = useMemo(
    () => (isJobFeatureRestricted ? METHODS.filter((item) => item.key !== 'candidate') : METHODS),
    [isJobFeatureRestricted]
  );

  const [method, setMethod] = useState<MatchingMethod | null>(null);
  const [resumeFileName, setResumeFileName] = useState<string | null>(null);
  // 履歴書から抽出した参照項目と、選択中の項目キー（初期は全選択）
  const [resumeFields, setResumeFields] = useState<ResumeField[]>([]);
  const [resumeFieldKeys, setResumeFieldKeys] = useState<string[]>([]);
  const [text, setText] = useState('');
  const [selectedCandidateId, setSelectedCandidateId] = useState<number | null>(null);
  const [selectedCandidateName, setSelectedCandidateName] = useState<string | null>(null);
  // 提案を開始したか（フォーム → 実行フェーズの切替）
  const [started, setStarted] = useState(false);
  const [selectedResultIds, setSelectedResultIds] = useState<Set<number>>(new Set());
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isPdfProcessing, setIsPdfProcessing] = useState(false);
  const [isPreparingShare, setIsPreparingShare] = useState(false);
  const [shareText, setShareText] = useState('');
  // カスタマイズ（登録済みの求職者から）の入力を保持し、再オープン時に復元する
  const [customizeSelection, setCustomizeSelection] = useState<ReferenceSelection>(EMPTY_SELECTION);
  const [customizePrompt, setCustomizePrompt] = useState('');

  const flow = useAiJobMatchingFlow();
  const { downloadMultiplePdfs } = usePdf();
  const importSharedJobPosts = useImportSharedJobPosts();
  const bulkJobPosts = useSuggestAiRecommendations(selectedCandidateId ?? 0);

  // 選択中の結果（共有・提案のプレビュー用）
  const selectedResults = useMemo(
    () => flow.results.filter((result) => selectedResultIds.has(result.id)),
    [flow.results, selectedResultIds]
  );

  // 求人DBの求人は自社求人として取り込み、選択順のまま自社求人の一覧にそろえる（WEB共有・PDF共有で共通）
  const resolveProvidedJobPosts = async (): Promise<AgentApiProvidedJobPostsRecord[]> => {
    const sharedIds = selectedResults.flatMap((r) =>
      r.source === 'shared' && r.shared_job_post_id !== null ? [r.shared_job_post_id] : []
    );
    const importedBySharedId = new Map<number, AgentApiProvidedJobPostsRecord>();
    if (sharedIds.length > 0) {
      const { provided_job_posts } = await importSharedJobPosts.mutateAsync({
        sharedJobPostIds: sharedIds,
      });
      provided_job_posts.forEach((post) => {
        if (post.shared_job_post) importedBySharedId.set(post.shared_job_post.id, post);
      });
    }

    return selectedResults.flatMap((result) => {
      const post =
        result.source === 'shared'
          ? importedBySharedId.get(result.shared_job_post_id ?? -1)
          : result.provided_job_post;
      return post ? [post] : [];
    });
  };

  // WEB共有: 公開URLではなく自社求人のURLで共有する
  const handleWebShare = async () => {
    if (isPreparingShare) return;

    setIsPreparingShare(true);
    try {
      const posts = await resolveProvidedJobPosts();
      const text = posts
        .map((post) => [post.company?.name, post.title, post.urls?.open].filter(Boolean).join('\n'))
        .join('\n\n');

      setShareText(text);
      setShareOpen(true);
    } catch (error) {
      console.error('求人の取り込みエラー:', error);
      showErrorToast('共有用の求人の準備に失敗しました。もう一度お試しください。');
    } finally {
      setIsPreparingShare(false);
    }
  };

  const resetInputs = () => {
    setResumeFileName(null);
    setResumeFields([]);
    setResumeFieldKeys([]);
    setText('');
    setSelectedCandidateId(null);
    setSelectedCandidateName(null);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setMethod(null);
      resetInputs();
      setStarted(false);
      flow.reset();
      setSelectedResultIds(new Set());
      setCustomizeOpen(false);
      setShareOpen(false);
      setConfirmOpen(false);
      setCustomizeSelection(EMPTY_SELECTION);
      setCustomizePrompt('');
    }
    onOpenChange(next);
  };

  const handleBackToMethod = () => {
    setMethod(null);
    resetInputs();
    setCustomizeSelection(EMPTY_SELECTION);
    setCustomizePrompt('');
  };

  // 入力種別ごとに実フローへ投入する
  const runMatching = (payload?: CustomizeResearchPayload) => {
    setSelectedResultIds(new Set());
    if (method === 'candidate' && selectedCandidateId !== null) {
      // カスタマイズ再検索時は入力を保持し、再オープン時に復元できるようにする
      const selection = payload
        ? (payload.referenceSelection ?? EMPTY_SELECTION)
        : customizeSelection;
      const prompt = payload ? (payload.prompt ?? '') : customizePrompt;
      if (payload) {
        setCustomizeSelection(selection);
        setCustomizePrompt(prompt);
      }
      void flow.submit({
        inputType: 'candidate',
        candidateProfileId: selectedCandidateId,
        sourceSelection: selection,
        prompt,
      });
    } else if (method === 'text') {
      const nextText = payload?.text ?? text;
      if (payload?.text !== undefined) setText(payload.text);
      void flow.submit({ inputType: 'free_text', freeText: nextText });
    } else if (method === 'resume' && resumeFields.length > 0) {
      // 選択した項目のみでペルソナを構築する（未選択なら全項目）
      const keys = payload?.resumeFieldKeys ?? resumeFieldKeys;
      if (payload?.resumeFieldKeys) setResumeFieldKeys(keys);
      const selected = resumeFields.filter((field) => keys.includes(field.key));
      const usable = selected.length > 0 ? selected : resumeFields;
      const freeText = usable.map((field) => `${field.label}: ${field.value}`).join('\n');
      void flow.submit({ inputType: 'free_text', freeText });
    }
  };

  // 提案を開始する
  const handleStart = () => {
    setStarted(true);
    runMatching();
  };

  // カスタマイズ条件で再検索する
  const handleResearch = (payload: CustomizeResearchPayload) => {
    setCustomizeOpen(false);
    runMatching(payload);
  };

  // WEB共有のURLをコピーした: トーストを出して一連のフローを閉じる
  const handleCopied = () => {
    showSuccessToast('共有URLをコピーしました。');
    handleOpenChange(false);
  };

  // PDF共有: 選択中の求人票を既存の一括PDFダウンロード（ZIP）と同じ挙動で1つのZIPにまとめる
  const handlePdfShare = async () => {
    if (isPdfProcessing || selectedResults.length === 0) return;

    setIsPdfProcessing(true);
    try {
      const posts = await resolveProvidedJobPosts();
      if (posts.length === 0) return;
      const result = await downloadMultiplePdfs(posts.map((post) => post.id));
      if (result.missingCount > 0) {
        showErrorToast(
          `${result.downloadedCount}件をダウンロードしました。${result.missingCount}件は取得できませんでした。`
        );
      }
    } catch (error) {
      console.error('PDFダウンロードエラー:', error);
      showErrorToast('PDFのダウンロードに失敗しました。もう一度お試しください。');
    } finally {
      setIsPdfProcessing(false);
    }
  };

  // 求人提案を追加する（候補者method）: 選択中の求人を候補者へ一括提案する
  const handleConfirmSuggest = () => {
    if (selectedCandidateId === null) return;
    if (selectedResults.length === 0) return;

    bulkJobPosts.mutate(selectedResults, {
      onSuccess: () => {
        showSuccessToast('提案求人を追加しました。');
        setConfirmOpen(false);
        handleOpenChange(false);
      },
      onError: () => {
        showErrorToast('提案求人の追加に失敗しました');
      },
    });
  };

  const canStart =
    (method === 'resume' && resumeFields.length > 0) ||
    (method === 'text' && text.trim().length > 0) ||
    (method === 'candidate' && selectedCandidateId !== null);

  const isMethodSelect = !started && method === null;

  // ステップ1がビューポートに収まらないときは中身ごと等倍で縮小する
  const [methodSelectNode, setMethodSelectNode] = useState<HTMLDivElement | null>(null);
  const [methodSelectHeight, setMethodSelectHeight] = useState(METHOD_SELECT_HEIGHT);
  const [methodSelectScale, setMethodSelectScale] = useState(1);

  useEffect(() => {
    if (!methodSelectNode) return undefined;
    const observer = new ResizeObserver(() => {
      setMethodSelectHeight(methodSelectNode.offsetHeight);
    });
    observer.observe(methodSelectNode);
    return () => observer.disconnect();
  }, [methodSelectNode]);

  useEffect(() => {
    if (!open || !isMethodSelect) return undefined;
    const update = () => {
      setMethodSelectScale(
        Math.min(
          1,
          (window.innerWidth - VIEWPORT_MARGIN) / METHOD_SELECT_WIDTH,
          (window.innerHeight - VIEWPORT_MARGIN) / methodSelectHeight
        )
      );
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [open, isMethodSelect, methodSelectHeight]);
  // 実行中は誤操作で中断されないよう、外側クリック・ESC で閉じない
  const isRunning = started && !flow.hasResults && flow.stage !== 'failed';

  // ステージ／選択した方法ごとに横幅を変える（Figma の各ダイアログ幅に準拠）。
  const dialogSizeClass = started
    ? flow.hasResults
      ? 'h-[680px] max-h-[90vh] max-w-[956px]'
      : 'max-h-[80vh] max-w-[700px]'
    : method === null
      ? ''
      : method === 'candidate'
        ? // 検索結果の件数でダイアログの高さが変わらないよう高さを固定する
          'h-[80vh] max-w-[min(1020px,80vw)]'
        : 'max-h-[80vh] max-w-[min(1032px,80vw)]';

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className={clsx(
          'flex w-full flex-col overflow-hidden p-0',
          dialogSizeClass,
          isMethodSelect ? 'rounded-[26px]' : 'rounded-md'
        )}
        // 担当者メニューは body 直下に portal されるため、Radix の外側判定でダイアログを閉じない
        onInteractOutside={(event) => {
          if (isRunning || (event.target as Element | null)?.closest?.('[data-users-menu]')) {
            event.preventDefault();
          }
        }}
        onEscapeKeyDown={(event) => {
          if (isRunning) event.preventDefault();
        }}
        // ステップ1のみ Figma の装飾背景（右上=紫 / 左下=青のソフトなグラデーション）
        style={
          isMethodSelect
            ? {
                width: METHOD_SELECT_WIDTH * methodSelectScale,
                height: methodSelectHeight * methodSelectScale,
                background:
                  'radial-gradient(130% 130% at 100% 0%, #f6f3ff 0%, rgba(246,243,255,0) 48%),' +
                  ' radial-gradient(130% 130% at 0% 100%, #edf8ff 0%, rgba(237,248,255,0) 48%), #ffffff',
              }
            : undefined
        }
      >
        {started && !flow.hasResults ? (
          // 実行中／失敗（既存の実行中パネルを再利用）。
          // 失敗時はエラー箱を内容高さで表示し（候補者側と同様）、ローディング時のみ min-h で高さを確保する。
          <>
            <DialogTitle className="sr-only">AI求人マッチングを実行中</DialogTitle>
            {flow.stage === 'failed' ? (
              <div className="p-lg">
                <RunStatusPanel
                  isLoading={false}
                  isFailed
                  errorMessage={flow.errorMessage}
                  onRetry={flow.retry}
                />
              </div>
            ) : (
              <div className="flex min-h-84.25 w-full flex-col">
                <RunStatusPanel
                  isLoading={flow.isLoading}
                  isFailed={false}
                  progressMessage={flow.progressMessage}
                  onRetry={flow.retry}
                  loadingTitle="AI求人マッチングを実行中"
                  fillParent
                />
              </div>
            )}
          </>
        ) : started && flow.hasResults ? (
          // 検索結果
          <div className="flex min-h-0 flex-1 flex-col">
            <div className="flex h-14 shrink-0 items-center border-b border-solid border-border px-lg">
              <DialogTitle className="min-w-0 truncate text-heading-xs text-main">
                {method === 'candidate' && selectedCandidateName
                  ? `${selectedCandidateName}さんへのAIおすすめ求人検索結果`
                  : 'AIおすすめ求人検索結果'}
              </DialogTitle>
            </div>
            <div className="hidden-scrollbar flex min-h-0 flex-1 flex-col overflow-y-auto bg-gray p-lg">
              <RecommendationList
                results={flow.results}
                selectedIds={selectedResultIds}
                onSelectedIdsChange={setSelectedResultIds}
              />
            </div>
            <div className="flex h-18.5 shrink-0 items-center justify-between border-t border-solid border-border px-lg py-md">
              {selectedResultIds.size > 0 ? (
                <div className="flex items-center gap-xs">
                  <p className="flex items-center gap-0.75 pr-md text-main">
                    <span className="text-heading-md">{selectedResultIds.size}件</span>
                    <span className="text-body-xs">選択中</span>
                  </p>
                  <Button
                    type="button"
                    color="secondary"
                    size="md"
                    onClick={() => setSelectedResultIds(new Set())}
                    aria-label="選択を解除する"
                  >
                    <MdLinkOff size={16} aria-hidden="true" />
                    選択解除
                  </Button>
                </div>
              ) : (
                <div />
              )}

              <div className="flex items-center gap-xs">
                <Button
                  type="button"
                  color="secondary"
                  size="md"
                  onClick={() => handleOpenChange(false)}
                >
                  {method === 'candidate' ? '閉じる' : 'キャンセル'}
                </Button>
                {selectedResultIds.size > 0 &&
                  (method === 'candidate' ? (
                    <Button
                      type="button"
                      color="primary"
                      size="md"
                      onClick={() => setConfirmOpen(true)}
                      disabled={bulkJobPosts.isPending}
                    >
                      {bulkJobPosts.isPending ? '提案中...' : '求人提案する'}
                    </Button>
                  ) : (
                    <>
                      <Button
                        type="button"
                        color="primary"
                        size="md"
                        onClick={handleWebShare}
                        disabled={isPreparingShare}
                      >
                        <MdLink size={20} aria-hidden="true" />
                        {isPreparingShare ? '準備中...' : 'WEB共有'}
                      </Button>
                      <Button
                        type="button"
                        color="primary"
                        size="md"
                        onClick={handlePdfShare}
                        disabled={isPdfProcessing}
                      >
                        <MdDescription size={20} aria-hidden="true" />
                        {isPdfProcessing ? '作成中...' : 'PDF共有'}
                      </Button>
                    </>
                  ))}
              </div>
            </div>
            <CustomizeFab
              className="absolute right-lg bottom-22.5"
              onClick={() => setCustomizeOpen(true)}
            />
            {method !== null && (
              <CustomizeModal
                open={customizeOpen}
                onOpenChange={setCustomizeOpen}
                method={method}
                candidateProfileId={selectedCandidateId}
                initialText={text}
                initialSelection={customizeSelection}
                initialPrompt={customizePrompt}
                resumeFields={resumeFields}
                initialResumeKeys={resumeFieldKeys}
                onResearch={handleResearch}
              />
            )}

            {/* 履歴書・テキストの場合: WEB共有（求人票のURLをコピーする） */}
            {method !== 'candidate' && (
              <ShareContentDialog
                title="求人票のURLをコピーする"
                shareText={shareText}
                open={shareOpen}
                onOpenChange={setShareOpen}
                onCopied={handleCopied}
              />
            )}

            {/* 求職者からの場合: 求人提案の確認 */}
            {method === 'candidate' && (
              <SuggestionConfirmDialog
                open={confirmOpen}
                onOpenChange={(next) => {
                  if (bulkJobPosts.isPending) return;
                  setConfirmOpen(next);
                }}
                selectedResults={selectedResults}
                isSubmitting={bulkJobPosts.isPending}
                onConfirm={handleConfirmSuggest}
              />
            )}
          </div>
        ) : method === null ? (
          // ステップ1: 方法を選ぶ（Figma 実寸で組み、収まらないビューポートでは等倍縮小する）
          <div
            ref={setMethodSelectNode}
            className="relative flex shrink-0 flex-col items-center px-16.5 py-13"
            style={{
              width: METHOD_SELECT_WIDTH,
              transform: `scale(${methodSelectScale})`,
              transformOrigin: 'top left',
            }}
          >
            <button
              type="button"
              onClick={() => handleOpenChange(false)}
              className="absolute top-lg right-lg flex size-10 items-center justify-center rounded-full bg-white text-sub shadow-layer1 hover:text-main"
              aria-label="閉じる"
            >
              <LuX className="size-icon-sm" />
            </button>

            <div className="flex items-center gap-lg">
              <div className="flex size-19 items-center justify-center rounded-full bg-white shadow-layer1">
                <GenerateIcon size={48} className="text-primary" />
              </div>
              <DialogTitle className="text-heading-lg text-black">AI求人マッチング</DialogTitle>
            </div>

            <div className="mt-lg">
              <StepIndicator activeUpTo={1} size="lg" />
            </div>

            <p className="mt-lg text-body-md text-black">
              どの情報をもとにして、AIが求人提案をしますか？
            </p>

            <div className="mt-md flex gap-lg">
              {availableMethods.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => setMethod(item.key)}
                  className="flex h-63.25 w-67.5 flex-col items-center justify-between overflow-hidden rounded-md bg-white px-md py-5.25 shadow-layer1 transition-shadow hover:shadow-layer2"
                >
                  <img
                    src={item.image}
                    width={item.width}
                    height={item.height}
                    alt=""
                    className="object-contain"
                  />
                  <div className="flex flex-col items-center gap-xs text-center">
                    <span className="text-heading-xs text-black">{item.title}</span>
                    <span className="w-59.5 text-body-xs text-main">{item.description}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        ) : (
          // ステップ2: 求職者を設定（ヘッダー・フッター固定、中身のみスクロール）
          <div className="flex min-h-0 flex-1 flex-col">
            <div className="flex shrink-0 flex-col gap-xs border-b border-solid border-border px-lg py-lg">
              <DialogTitle className="text-heading-xs! text-black">AI求人マッチング</DialogTitle>
              <StepIndicator
                activeUpTo={canStart ? 3 : 2}
                size="sm"
                onBackToMethod={handleBackToMethod}
              />
            </div>

            <div
              className={clsx(
                'flex min-h-0 flex-1 flex-col',
                method !== 'candidate' && 'hidden-scrollbar overflow-y-auto px-xxl pt-sm pb-xxl'
              )}
            >
              {method === 'resume' && (
                <ResumeUploadStep
                  fileName={resumeFileName}
                  onChange={(value) => {
                    setResumeFileName(value?.fileName ?? null);
                    setResumeFields(value?.fields ?? []);
                    // アップロード直後は全項目を選択状態にする
                    setResumeFieldKeys((value?.fields ?? []).map((field) => field.key));
                  }}
                />
              )}
              {method === 'text' && <TextInputStep value={text} onChange={setText} />}
              {method === 'candidate' && !isJobFeatureRestricted && (
                <CandidateSelectStep
                  selectedId={selectedCandidateId}
                  onSelect={(id, name) => {
                    setSelectedCandidateId(id);
                    setSelectedCandidateName(name);
                  }}
                />
              )}
            </div>

            <div className="flex shrink-0 justify-end gap-md border-t border-solid border-border px-lg py-md">
              <Button
                type="button"
                color="secondary"
                size="md"
                onClick={() => handleOpenChange(false)}
              >
                閉じる
              </Button>
              <Button
                type="button"
                color="primary"
                size="md"
                disabled={!canStart}
                onClick={handleStart}
              >
                <LuSparkles className="size-icon-sm" />
                提案を開始する
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
